import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { integrationAPI, productAPI } from "@/services/api";
import { isValidProductId } from "@/lib/productId";
import {
  ArrowLeft,
  Save,
  Globe,
  Shield,
  Link2,
  Plus,
  Trash2,
  Play,
  Check,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Zap,
  GripVertical,
} from "lucide-react";

/* ─── SVG Connector ──────────────────────────────── */
const FlowConnector = ({ label, height = 48 }) => (
  <div className="relative flex justify-center" style={{ height }}>
    <svg
      width="3"
      height={height}
      className="overflow-visible"
    >
      <line
        x1="1.5"
        y1="0"
        x2="1.5"
        y2={height - 10}
        stroke="#9cb9ff"
        strokeWidth="3"
        strokeDasharray="6 4"
      />
      <polygon
        points={`-5,${height - 12} 1.5,${height} 8,${height - 12}`}
        fill="#465fff"
      />
    </svg>
    {label && (
      <span className="absolute top-1/2 -translate-y-1/2 bg-white px-2 text-[10px] font-semibold text-brand-500 tracking-wider uppercase">
        {label}
      </span>
    )}
  </div>
);

/* ─── Step Number Badge ──────────────────────────── */
const StepBadge = ({ number }) => (
  <div className="absolute -left-4 top-3 w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-semibold flex items-center justify-center shadow-theme-xs z-10">
    {number}
  </div>
);

/* ─── Status Glow Class ──────────────────────────── */
const getGlowClass = (status) => {
  if (status === "success") return "ring-2 ring-emerald-300 shadow-emerald-100 shadow-lg";
  if (status === "failed") return "ring-2 ring-red-300 shadow-red-100 shadow-lg";
  return "";
};

/* ─── Test Status Badge ──────────────────────────── */
const TestStatusBadge = ({ status }) => {
  if (!status) return null;
  if (status === "testing")
    return (
      <Badge variant="secondary" className="text-[10px] animate-pulse h-5">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        Testing
      </Badge>
    );
  if (status === "success")
    return (
      <Badge className="text-[10px] bg-emerald-500 hover:bg-emerald-600 h-5">
        <Check className="w-3 h-3 mr-1" />
        Connected
      </Badge>
    );
  return (
    <Badge variant="destructive" className="text-[10px] h-5">
      <X className="w-3 h-3 mr-1" />
      Failed
    </Badge>
  );
};

/* ─── Flow Node Box ──────────────────────────────── */
const FlowNodeBox = ({
  step,
  title,
  icon: Icon,
  children,
  headerRight,
  glowStatus,
  collapsed,
  onToggleCollapse,
  collapsedPreview,
}) => (
  <div
    className={`relative w-full max-w-2xl mx-auto rounded-2xl border border-gray-200 bg-white shadow-theme-xs transition-all duration-300 hover:shadow-theme-md ${getGlowClass(glowStatus)}`}
  >
    {step && <StepBadge number={step} />}
    {/* Header */}
    <div
      className="flex items-center justify-between px-4 py-2.5 bg-brand-500 rounded-t-2xl cursor-pointer"
      onClick={onToggleCollapse}
    >
      <div className="flex items-center gap-2 text-white">
        <Icon className="w-4 h-4" />
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {headerRight}
        {onToggleCollapse && (
          <div className="text-white/70 hover:text-white transition-colors">
            {collapsed ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
          </div>
        )}
      </div>
    </div>
    {/* Content */}
    {collapsed ? (
      collapsedPreview && (
        <div className="px-4 py-2 text-xs text-muted-foreground border-t border-gray-100 bg-brand-50/50">
          {collapsedPreview}
        </div>
      )
    ) : (
      <div className="p-4">{children}</div>
    )}
  </div>
);

const mapAuthTypeForUi = (authType) => {
  if (authType === "jwt") return "bearer";
  return authType || "none";
};

const mapAuthTypeForApi = (authType) => {
  if (authType === "bearer" || authType === "oauth") return "jwt";
  if (!authType || authType === "none") return null;
  return authType;
};

const normalizeEndpoint = (ep) => ({
  ...ep,
  id: ep.id || crypto.randomUUID(),
  method: ep.method || "GET",
  headers: ep.headers || null,
  body: ep.body || null,
  query_params: ep.queryParams || ep.query_params || [],
  expected_response: ep.expectedResponse || ep.expected_response || "",
  description: ep.description || "",
});

const buildEndpointTestPayload = (ep, baseUrl, authType, authValue) => {
  const params = (ep.query_params || []).reduce((acc, param) => {
    if (param?.key) acc[param.key] = param.value ?? "";
    return acc;
  }, {});

  return {
    baseUrl,
    authType: mapAuthTypeForApi(authType),
    authValue: authValue || null,
    path: ep.path,
    method: ep.method,
    headers: typeof ep.headers === "object" ? ep.headers : null,
    body: ep.body,
    params,
  };
};

const buildIntegrationSavePayload = (baseUrl, authType, authValue, endpoints) => ({
  baseUrl,
  authType: mapAuthTypeForApi(authType),
  authValue: authValue || null,
  endpoints: endpoints.map((ep) => ({
    id: ep.id,
    name: ep.name,
    path: ep.path,
    method: ep.method,
    headers: typeof ep.headers === "object" ? ep.headers : null,
    body: ep.body,
    queryParams: ep.query_params,
    expectedResponse: ep.expected_response,
    description: ep.description,
  })),
});
const ClientIntegrationBuilder = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Config
  const [baseUrl, setBaseUrl] = useState("");
  const [authType, setAuthType] = useState("none");
  const [authValue, setAuthValue] = useState("");
  const [endpoints, setEndpoints] = useState([]);

  // Test
  const [baseTestStatus, setBaseTestStatus] = useState(null);
  const [baseTestResult, setBaseTestResult] = useState(null);
  const [epTestStatus, setEpTestStatus] = useState({});
  const [epTestResults, setEpTestResults] = useState({});

  // Collapsed state for endpoints
  const [collapsedEps, setCollapsedEps] = useState({});
  const [expandedAdvanced, setExpandedAdvanced] = useState({});

  /* ─── Fetch ────────────────── */
  const fetchData = useCallback(async () => {
    if (!isValidProductId(productId)) return;

    setLoading(true);
    try {
      const [pRes, iRes] = await Promise.all([
        productAPI.getProduct(productId),
        integrationAPI.getIntegration(productId),
      ]);
      setProduct(pRes.data?.product || pRes.data);
      const c = iRes.data?.integration || iRes.data || {};
      setBaseUrl(c.baseUrl || c.base_url || "");
      setAuthType(mapAuthTypeForUi(c.authType || c.auth_type));
      setAuthValue(c.authValue || c.auth_value || "");
      const eps =
        c.endpoints?.length > 0
          ? c.endpoints.map((ep) => normalizeEndpoint(ep))
          : [];
      setEndpoints(eps);
      // Collapse all existing endpoints by default
      const collapsed = {};
      eps.forEach((ep) => {
        collapsed[ep.id] = true;
      });
      setCollapsedEps(collapsed);
    } catch {
      toast.error("Failed to load integration config");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (!isValidProductId(productId)) {
      toast.error("Invalid product — choose an offer first");
      navigate("/client/products", { replace: true });
      return;
    }
    fetchData();
  }, [productId, fetchData, navigate]);

  const markChanged = () => setHasChanges(true);

  /* ─── Base URL ─────────────── */
  const handleBaseUrlChange = (val) => {
    setBaseUrl(val);
    setBaseTestStatus(null);
    markChanged();
  };

  /* ─── Auth ─────────────────── */
  const handleAuthTypeChange = (val) => {
    setAuthType(val);
    setBaseTestStatus(null);
    markChanged();
  };
  const handleAuthValueChange = (val) => {
    setAuthValue(val);
    setBaseTestStatus(null);
    markChanged();
  };

  /* ─── Test Base ────────────── */
  const handleTestBase = async () => {
    if (!isValidProductId(productId)) {
      toast.error("Invalid product");
      return;
    }
    if (!baseUrl) {
      toast.error("Enter a base URL first");
      return;
    }
    setBaseTestStatus("testing");
    setBaseTestResult(null);
    try {
      const res = await integrationAPI.testBaseUrl(productId, baseUrl, authType, authValue || null);
      const r = res.data;
      setBaseTestStatus(r.success ? "success" : "failed");
      setBaseTestResult(r);
      if (r.success) toast.success(`Connected! ${r.status_code} (${r.response_time_ms}ms)`);
      else toast.error(r.message);
    } catch {
      setBaseTestStatus("failed");
      toast.error("Test failed");
    }
  };

  /* ─── Endpoints ────────────── */
  const addEndpoint = () => {
    const id = crypto.randomUUID();
    setEndpoints([
      ...endpoints,
      {
        id,
        name: "",
        path: "",
        method: "GET",
        headers: null,
        body: null,
        query_params: [],
        expected_response: "",
        description: "",
      },
    ]);
    setCollapsedEps({ ...collapsedEps, [id]: false }); // New one is expanded
    markChanged();
  };

  const updateEp = (id, field, value) => {
    setEndpoints(endpoints.map((ep) => (ep.id === id ? { ...ep, [field]: value } : ep)));
    setEpTestStatus({ ...epTestStatus, [id]: null });
    markChanged();
  };

  const removeEp = (id) => {
    setEndpoints(endpoints.filter((ep) => ep.id !== id));
    markChanged();
  };

  const toggleCollapse = (id) => setCollapsedEps({ ...collapsedEps, [id]: !collapsedEps[id] });
  const toggleAdvanced = (id) => setExpandedAdvanced({ ...expandedAdvanced, [id]: !expandedAdvanced[id] });

  /* Query params */
  const addQP = (epId) => {
    setEndpoints(
      endpoints.map((ep) =>
        ep.id === epId
          ? { ...ep, query_params: [...(ep.query_params || []), { key: "", value: "" }] }
          : ep
      )
    );
    markChanged();
  };
  const updateQP = (epId, i, field, value) => {
    setEndpoints(
      endpoints.map((ep) => {
        if (ep.id !== epId) return ep;
        const params = [...(ep.query_params || [])];
        params[i] = { ...params[i], [field]: value };
        return { ...ep, query_params: params };
      })
    );
    markChanged();
  };
  const removeQP = (epId, i) => {
    setEndpoints(
      endpoints.map((ep) => {
        if (ep.id !== epId) return ep;
        const params = [...(ep.query_params || [])];
        params.splice(i, 1);
        return { ...ep, query_params: params };
      })
    );
    markChanged();
  };

  /* ─── Test Endpoint ────────── */
  const handleTestEndpoint = async (ep) => {
    if (!isValidProductId(productId)) {
      toast.error("Invalid product");
      return;
    }
    if (!baseUrl && !ep.path.startsWith("http")) {
      toast.error("Set a base URL first");
      return;
    }
    if (!ep.path) {
      toast.error("Enter an endpoint path");
      return;
    }
    setEpTestStatus({ ...epTestStatus, [ep.id]: "testing" });
    setEpTestResults({ ...epTestResults, [ep.id]: null });
    try {
      const res = await integrationAPI.testEndpoint(
        productId,
        buildEndpointTestPayload(ep, baseUrl, authType, authValue)
      );
      const r = res.data;
      setEpTestStatus({ ...epTestStatus, [ep.id]: r.success ? "success" : "failed" });
      setEpTestResults({ ...epTestResults, [ep.id]: r });
      if (r.success) toast.success(`${ep.name || "Endpoint"}: ${r.status_code} (${r.response_time_ms}ms)`);
      else toast.error(`${ep.name || "Endpoint"}: ${r.message}`);
    } catch {
      setEpTestStatus({ ...epTestStatus, [ep.id]: "failed" });
      toast.error("Test failed");
    }
  };

  /* ─── Save ─────────────────── */
  const handleSave = async () => {
    if (!isValidProductId(productId)) {
      toast.error("Invalid product");
      navigate("/client/products", { replace: true });
      return;
    }
    if (!baseUrl) {
      toast.error("Base URL is required");
      return;
    }
    for (const ep of endpoints) {
      if (!ep.name.trim()) {
        toast.error("All endpoints must have a name");
        return;
      }
      if (!ep.path.trim()) {
        toast.error(`Endpoint "${ep.name}" needs a path`);
        return;
      }
    }
    setSaving(true);
    try {
      await integrationAPI.saveIntegration(
        productId,
        buildIntegrationSavePayload(baseUrl, authType, authValue, endpoints)
      );
      setHasChanges(false);
      toast.success("Integration saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const authLabels = { none: "No Authentication", bearer: "JWT / Bearer Token", api_key: "API Key", oauth: "OAuth Access Token" };
  const authPlaceholders = { none: "", bearer: "Paste your JWT or Bearer token here", api_key: "Paste your API key here", oauth: "Paste your OAuth access token here" };
  const methodColors = { GET: "text-emerald-600 bg-emerald-50", POST: "text-brand-500 bg-brand-50", PUT: "text-amber-600 bg-amber-50", DELETE: "text-red-600 bg-red-50" };

  /* ─── Loading ──────────────── */
  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto py-8">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-44 w-full rounded-xl" />
        <Skeleton className="h-36 w-full rounded-xl" />
      </div>
    );
  }

  /* ═══ RENDER ════════════════════════════════════ */
  return (
    <div className="min-h-screen min-w-0 pb-24">
      {/* Sticky Top Bar */}
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white/90 px-0 backdrop-blur-md">
        <div className="flex items-center justify-between py-3 max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/client/products")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-foreground leading-tight">Integration Builder</h1>
              <p className="text-[11px] text-muted-foreground">{product?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Badge variant="secondary" className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200">
                Unsaved changes
              </Badge>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-brand-500 hover:bg-brand-600 shadow-theme-xs"
              data-testid="save-integration-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile title */}
      <div className="sm:hidden mt-4 mb-2 max-w-3xl mx-auto">
        <h1 className="text-lg font-bold text-foreground">Integration Builder</h1>
        <p className="text-xs text-muted-foreground">{product?.name}</p>
      </div>

      {/* ─── FLOW DIAGRAM ───────────────────────── */}
      <div className="flex flex-col items-center mt-6 space-y-0 pl-4">
        {/* ── 1. Base URL ────────── */}
        <FlowNodeBox
          step={1}
          title="Base URL"
          icon={Globe}
          glowStatus={baseTestStatus}
          headerRight={<TestStatusBadge status={baseTestStatus} />}
        >
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Enter the root URL of your API
            </Label>
            <Input
              placeholder="https://api.yourcompany.com"
              value={baseUrl}
              onChange={(e) => handleBaseUrlChange(e.target.value)}
              className="font-mono text-sm border-gray-200 focus:border-brand-400"
              data-testid="base-url-input"
            />
            <p className="text-[10px] text-muted-foreground">
              All endpoint paths below will be appended to this URL.
            </p>
          </div>
        </FlowNodeBox>

        <FlowConnector label="then" />

        {/* ── 2. Authentication ──── */}
        <FlowNodeBox step={2} title="Authentication" icon={Shield}>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">
                How does your API authenticate requests?
              </Label>
              <Select value={authType} onValueChange={handleAuthTypeChange}>
                <SelectTrigger data-testid="auth-type-select" className="border-gray-200">
                  <SelectValue placeholder="Select auth type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Authentication</SelectItem>
                  <SelectItem value="bearer">JWT / Bearer Token</SelectItem>
                  <SelectItem value="api_key">API Key</SelectItem>
                  <SelectItem value="oauth">OAuth Access Token</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {authType !== "none" && (
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">
                  {authLabels[authType]}
                </Label>
                <Input
                  type="password"
                  placeholder={authPlaceholders[authType]}
                  value={authValue}
                  onChange={(e) => handleAuthValueChange(e.target.value)}
                  className="font-mono text-sm border-gray-200 focus:border-brand-400"
                  data-testid="auth-value-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Stored securely and never exposed.
                </p>
              </div>
            )}
          </div>
        </FlowNodeBox>

        <FlowConnector />

        {/* ── Test Connection ────── */}
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center">
          <Button
            variant="outline"
            onClick={handleTestBase}
            disabled={!baseUrl || baseTestStatus === "testing"}
            className={`px-8 py-2 border-2 font-semibold transition-all ${
              baseTestStatus === "success"
                ? "border-emerald-400 text-emerald-600 bg-emerald-50 shadow-emerald-100 shadow-md"
                : baseTestStatus === "failed"
                  ? "border-red-400 text-red-600 bg-red-50 shadow-red-100 shadow-md"
                  : "border-brand-300 text-brand-500 hover:bg-brand-50"
            }`}
            data-testid="test-connection-btn"
          >
            {baseTestStatus === "testing" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : baseTestStatus === "success" ? (
              <Check className="w-4 h-4 mr-2" />
            ) : baseTestStatus === "failed" ? (
              <X className="w-4 h-4 mr-2" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Test Connection
          </Button>
          {baseTestResult && (
            <div
              className={`mt-2 text-xs px-3 py-1 rounded-full font-medium ${
                baseTestResult.success
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {baseTestResult.success
                ? `${baseTestResult.status_code} OK — ${baseTestResult.response_time_ms}ms`
                : baseTestResult.message}
            </div>
          )}
        </div>

        {/* ── Endpoints ─────────── */}
        {endpoints.length > 0 && <FlowConnector label="endpoints" height={52} />}

        {endpoints.map((ep, index) => (
          <div key={ep.id} className="flex flex-col items-center w-full">
            {index > 0 && <FlowConnector height={40} />}

            <FlowNodeBox
              step={3 + index}
              title={ep.name || `Endpoint ${index + 1}`}
              icon={Link2}
              glowStatus={epTestStatus[ep.id]}
              collapsed={collapsedEps[ep.id]}
              onToggleCollapse={() => toggleCollapse(ep.id)}
              collapsedPreview={
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${methodColors[ep.method] || ""}`}>
                    {ep.method}
                  </span>
                  <span className="font-mono text-[11px] text-gray-500 truncate">
                    {ep.path || "no path"}
                  </span>
                  {epTestStatus[ep.id] === "success" && (
                    <Check className="w-3 h-3 text-emerald-500 ml-auto shrink-0" />
                  )}
                </div>
              }
              headerRight={
                <div className="flex items-center gap-1">
                  <TestStatusBadge status={epTestStatus[ep.id]} />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-white/70 hover:text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeEp(ep.id);
                    }}
                    data-testid={`remove-endpoint-${index}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              }
            >
              <div className="space-y-3">
                {/* Name */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    Endpoint Name
                  </Label>
                  <Input
                    placeholder="e.g., Check Enrollment Status"
                    value={ep.name}
                    onChange={(e) => updateEp(ep.id, "name", e.target.value)}
                    className="text-sm border-gray-200 focus:border-brand-400"
                    data-testid={`endpoint-name-${index}`}
                  />
                </div>

                {/* Method + Path */}
                <div className="flex gap-2">
                  <div className="w-24 shrink-0">
                    <Label className="text-xs text-muted-foreground mb-1 block">Method</Label>
                    <Select value={ep.method} onValueChange={(v) => updateEp(ep.id, "method", v)}>
                      <SelectTrigger className="text-xs border-gray-200" data-testid={`endpoint-method-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="DELETE">DELETE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1 block">
                      Path or Full URL
                    </Label>
                    <Input
                      placeholder="/api/check-enrollment or https://..."
                      value={ep.path}
                      onChange={(e) => updateEp(ep.id, "path", e.target.value)}
                      className="font-mono text-sm border-gray-200 focus:border-brand-400"
                      data-testid={`endpoint-path-${index}`}
                    />
                  </div>
                </div>

                {/* Helper */}
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                  <HelpCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-700 leading-relaxed">
                    Does this endpoint need an ID or query parameter? Click "Advanced Settings" below to add them.
                  </p>
                </div>

                {/* Advanced Toggle */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleAdvanced(ep.id)}
                  className="text-xs text-muted-foreground w-full justify-between hover:bg-brand-50"
                >
                  <span>Advanced Settings</span>
                  {expandedAdvanced[ep.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </Button>

                {expandedAdvanced[ep.id] && (
                  <div className="space-y-3 pt-2 border-t border-dashed border-gray-200">
                    {/* Query Params */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs text-muted-foreground">Query Parameters</Label>
                        <Button variant="ghost" size="sm" className="h-6 text-[11px] text-brand-500" onClick={() => addQP(ep.id)}>
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      {(ep.query_params || []).map((param, pi) => (
                        <div key={pi} className="flex gap-2 mb-1.5">
                          <Input
                            placeholder="key"
                            value={param.key}
                            onChange={(e) => updateQP(ep.id, pi, "key", e.target.value)}
                            className="text-xs h-8 border-gray-200 focus:border-brand-400"
                          />
                          <Input
                            placeholder="value"
                            value={param.value}
                            onChange={(e) => updateQP(ep.id, pi, "value", e.target.value)}
                            className="text-xs h-8 border-gray-200 focus:border-brand-400"
                          />
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-red-400" onClick={() => removeQP(ep.id, pi)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Headers */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Custom Headers (JSON)</Label>
                      <Textarea
                        placeholder='{"X-Custom-Header": "value"}'
                        value={ep.headers ? JSON.stringify(ep.headers, null, 2) : ""}
                        onChange={(e) => {
                          try {
                            updateEp(ep.id, "headers", e.target.value ? JSON.parse(e.target.value) : null);
                          } catch {
                            updateEp(ep.id, "headers", e.target.value);
                          }
                        }}
                        className="font-mono text-xs h-16 border-gray-200 focus:border-brand-400"
                      />
                    </div>

                    {/* Body */}
                    {(ep.method === "POST" || ep.method === "PUT") && (
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Request Body (JSON)</Label>
                        <Textarea
                          placeholder='{"phone_hash": "test123"}'
                          value={ep.body ? JSON.stringify(ep.body, null, 2) : ""}
                          onChange={(e) => {
                            try {
                              updateEp(ep.id, "body", e.target.value ? JSON.parse(e.target.value) : null);
                            } catch {
                              updateEp(ep.id, "body", e.target.value);
                            }
                          }}
                          className="font-mono text-xs h-16 border-gray-200 focus:border-brand-400"
                        />
                      </div>
                    )}

                    {/* Expected Response */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Expected Response</Label>
                      <Textarea
                        placeholder='{"status": "active", "subscription_amount": 5000}'
                        value={ep.expected_response || ""}
                        onChange={(e) => updateEp(ep.id, "expected_response", e.target.value)}
                        className="font-mono text-xs h-16 border-gray-200 focus:border-brand-400"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Description</Label>
                      <Input
                        placeholder="What does this endpoint do?"
                        value={ep.description || ""}
                        onChange={(e) => updateEp(ep.id, "description", e.target.value)}
                        className="text-xs border-gray-200 focus:border-brand-400"
                      />
                    </div>
                  </div>
                )}

                {/* Test + Result */}
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestEndpoint(ep)}
                    disabled={!ep.path || epTestStatus[ep.id] === "testing"}
                    className={`text-xs border-2 font-medium ${
                      epTestStatus[ep.id] === "success"
                        ? "border-emerald-300 text-emerald-600 bg-emerald-50"
                        : epTestStatus[ep.id] === "failed"
                          ? "border-red-300 text-red-600 bg-red-50"
                          : "border-gray-200 text-brand-500"
                    }`}
                    data-testid={`test-endpoint-${index}`}
                  >
                    {epTestStatus[ep.id] === "testing" ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Play className="w-3 h-3 mr-1" />
                    )}
                    Test
                  </Button>
                  {epTestResults[ep.id] && (
                    <span
                      className={`text-[11px] font-medium ${
                        epTestResults[ep.id].success ? "text-emerald-600" : "text-red-600"
                      }`}
                    >
                      {epTestResults[ep.id].success
                        ? `${epTestResults[ep.id].status_code} — ${epTestResults[ep.id].response_time_ms}ms`
                        : epTestResults[ep.id].message}
                    </span>
                  )}
                </div>

                {/* Response Preview */}
                {epTestResults[ep.id]?.response_body && (
                  <div className="mt-2 p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                    <Label className="text-[9px] text-muted-foreground mb-1 block uppercase tracking-widest font-bold">
                      Response Preview
                    </Label>
                    <pre className="text-[11px] font-mono text-gray-600 whitespace-pre-wrap max-h-28 overflow-auto">
                      {typeof epTestResults[ep.id].response_body === "string"
                        ? epTestResults[ep.id].response_body
                        : JSON.stringify(epTestResults[ep.id].response_body, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </FlowNodeBox>
          </div>
        ))}

        {/* ── Add Endpoint ──────── */}
        {(endpoints.length > 0 || baseUrl) && <FlowConnector height={40} />}

        <div className="w-full max-w-2xl mx-auto flex justify-center">
          <Button
            variant="outline"
            onClick={addEndpoint}
            className="border-2 border-dashed border-brand-300 text-brand-500 hover:bg-brand-50 hover:border-brand-400 px-10 py-2.5 font-semibold"
            data-testid="add-endpoint-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Endpoint
          </Button>
        </div>

        {/* ── Summary ───────────── */}
        {(baseUrl || endpoints.length > 0) && (
          <>
            <div className="w-0.5 h-8 bg-brand-200 mx-auto" />
            <div className="w-full max-w-2xl mx-auto p-4 rounded-2xl bg-brand-50 border border-brand-200 shadow-theme-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-brand-500 flex items-center justify-center shadow-theme-xs">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Integration Summary</p>
                    <p className="text-xs text-brand-500">
                      {endpoints.length} endpoint{endpoints.length !== 1 ? "s" : ""} configured
                      {baseTestStatus === "success" && " — Base connected"}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  size="sm"
                  className="bg-brand-500 hover:bg-brand-600 shadow-theme-xs"
                  data-testid="save-integration-bottom-btn"
                >
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Empty state */}
        {!baseUrl && endpoints.length === 0 && !loading && (
          <div className="text-center py-10 max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-brand-500" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Set Up Your Integration</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Start by entering your API's base URL above. Then add the endpoints that Proovam should call to verify agent actions and subscriptions.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientIntegrationBuilder;
